import {
  Component,
  Inject,
  Injectable,
  Input,
  LOCALE_ID,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { CurrencyPipe, formatDate } from '@angular/common';
import {
  NgbActiveModal,
  NgbDate,
  NgbDatepickerI18n,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HotelRoom } from '@sabstravtech/obtservices/angular';
import { HotelQuoteResultWithExtension } from '@sabstravtech/obtservices/base';

import { LightningModalTypes } from '../../../../../core/classes/modal-types.enum';
import { Helpers } from '../../../../../core/classes/helpers';
import { resultIcons } from '../../../../../core/enum/result-icons.enum';

const I18N_VALUES = {
  weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ],
  shortMonthNames: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ]
};

@Injectable()
export class PriceBreakdownDatepickerI18n extends NgbDatepickerI18n {
  private hotelRoom: HotelRoom = null;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private translateService: TranslateService,
    private currencyPipe: CurrencyPipe
  ) {
    super();
  }

  setHotelRoom(room: HotelRoom) {
    this.hotelRoom = room;
  }

  getWeekdayLabel(weekday: number): string {
    return this.translateService.instant(I18N_VALUES.weekdays[weekday - 1] || '');
  }

  getMonthFullName(month: number): string {
    return this.translateService.instant(I18N_VALUES.months[month - 1] || '');
  }

  getWeekdayShortName(weekday: number): string {
    return this.getWeekdayLabel(weekday);
  }

  getMonthShortName(month: number): string {
    return this.translateService.instant(I18N_VALUES.shortMonthNames[month - 1] || '');
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    let dateFormatted: string;
    try {
      dateFormatted = formatDate(jsDate, 'fullDate', this.locale);
    } catch {
      dateFormatted = `${date.day}-${date.month}-${date.year}`;
    }

    if (this.hotelRoom?.nights) {
      const matchingNight = this.hotelRoom.nights.find(
        (night) =>
          night.date.day === date.day &&
          night.date.month === date.month &&
          night.date.year === date.year
      );

      if (matchingNight && matchingNight.rate != null) {
        const price = this.currencyPipe.transform(
          matchingNight.rate,
          this.hotelRoom.currencyCode,
          'symbol',
          '1.2-2'
        );
        return `${dateFormatted}, ${price}`;
      }
    }

    return dateFormatted;
  }
}

@Component({
  selector: 'app-price-breakdown-dialog',
  templateUrl: './price-breakdown-dialog.component.html',
  styleUrls: ['./price-breakdown-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    CurrencyPipe,
    PriceBreakdownDatepickerI18n,
    { provide: NgbDatepickerI18n, useExisting: PriceBreakdownDatepickerI18n }
  ],
  standalone: false
})
export class PriceBreakdownDialogComponent implements OnInit {
  @Input() public room: HotelRoom = null;
  @Input() public hotel: HotelQuoteResultWithExtension = {} as HotelQuoteResultWithExtension;
  public hotelRoom: HotelRoom = null;

  firstDay: string;
  lastDay: string;
  numberOfNights: number;
  resultItemType = resultIcons;

  constructor(
    public activeModal: NgbActiveModal,
    private i18n: PriceBreakdownDatepickerI18n
  ) {}

  ngOnInit() {
    if (this.room) {
      this.numberOfNights = this.room.nights.length;
      this.firstDay = this.room.nights[0].date;
      this.lastDay = this.room.nights[this.numberOfNights - 1].date;

      this.hotelRoom = Helpers.clone(this.room);
      this.hotelRoom.nights.forEach((night) => {
        night.date = Helpers.yyyymmddToNgDate(Helpers.dateStringToyyyymmdd(night.date));
      });
      this.i18n.setHotelRoom(this.hotelRoom);
    }
  }

  isDisabled = (date: NgbDate) =>
    this.hotelRoom.nights.every(
      (night) =>
        night.date.day !== date.day ||
        night.date.month !== date.month ||
        night.date.year !== date.year
    );
}

LightningModalTypes.PriceBreakdownDialogComponent.component = PriceBreakdownDialogComponent;
